
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAllTaskSubmissions,
  updateTaskStatus,
  bulkUpdateTaskStatus,
} from "../redux/actions/taskActions";
import Papa from "papaparse";
import { fetchDesignations } from "../redux/actions/designationActions";
import {
  FiSearch,
  FiDownload,
  FiFilter,
  FiX,
  FiCheck,
  FiXCircle,
  FiClock,
  FiCalendar,
  FiUser,
  FiPhone,
  FiBriefcase,
  FiImage,
  FiChevronLeft,
  FiChevronRight
} from "react-icons/fi";

const formatIndianDate = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const Task = () => {
  const dispatch = useDispatch();

  const {
    items: taskSubmissions = [],
    pagination = {},
    loading,
    error,
  } = useSelector((state) => state.task.taskSubmissions || {});

  const { designations = [] } = useSelector((state) => state.designation || {});

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [selectedDesignation, setSelectedDesignation] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const fetchTasks = (page = currentPage) => {
    dispatch(
      fetchAllTaskSubmissions({
        page,
        limit: itemsPerPage,
        fromDate,
        toDate,
        search: searchTerm,
        designation: selectedDesignation,
        status: selectedStatus,
      })
    );
  };

  useEffect(() => {
    dispatch(fetchDesignations());
  }, [dispatch]);

  useEffect(() => {
    fetchTasks();
  }, [
    currentPage,
    fromDate,
    toDate,
    searchTerm,
    selectedDesignation,
    selectedStatus,
  ]);

  const handleExportCSV = () => {
    const exportData = taskSubmissions.map((task) => ({
      TaskTitle: task.task_title || "-",
      TaskName: task.task_name || "-",
      Name: task.user_name || "-",
      Designation: task.designation || "-",
      Phone: task.user_phone || "-",
      Date: formatIndianDate(task.date),
      Time: task.time || "-",
      TaskPeriod: `${formatIndianDate(task.createTaskDate)} - ${formatIndianDate(task.endTaskDate)}`,
      Status: task.status || "-",
      PhotoURL: task.photo || "-",
    }));

    const csv = Papa.unparse(exportData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Task_Report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleSelectAll = () => {
    const allIds = taskSubmissions.map((item) => item.id);
    const allSelected = allIds.every((id) => selectedTasks.includes(id));
    if (allSelected) {
      setSelectedTasks((prev) => prev.filter((id) => !allIds.includes(id)));
    } else {
      setSelectedTasks((prev) => [...new Set([...prev, ...allIds])]);
    }
  };

  const toggleSelectOne = (id) => {
    if (selectedTasks.includes(id)) {
      setSelectedTasks((prev) => prev.filter((itemId) => itemId !== id));
    } else {
      setSelectedTasks((prev) => [...prev, id]);
    }
  };

  const handleBulkUpdate = (status) => {
    dispatch(bulkUpdateTaskStatus(selectedTasks, status)).then(() => {
      setSelectedTasks([]);
      fetchTasks(currentPage);
    });
  };

  const handleStatusUpdate = (taskId, status) => {
    dispatch(updateTaskStatus(taskId, status)).then(() => {
      fetchTasks(currentPage);
    });
  };

  const clearFilters = () => {
    setFromDate("");
    setToDate("");
    setSelectedDesignation("");
    setSelectedStatus("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300", label: "Pending" },
      approved: { color: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300", label: "Approved" },
      rejected: { color: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300", label: "Rejected" },
      ongoing: { color: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300", label: "Ongoing" },
      complete: { color: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300", label: "Complete" },
    };

    const config = statusConfig[status] || { color: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300", label: status };
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color} capitalize`}>
        {config.label}
      </span>
    );
  };

  const totalPages = Math.ceil(
    (pagination?.total || 0) / (pagination?.limit || itemsPerPage)
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400 text-lg">Loading Tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Task Submissions
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage and review all task submissions
          </p>
        </div>
        <div className="flex items-center gap-3 mt-4 lg:mt-0">
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-xl transition-colors"
          >
            <FiDownload className="text-lg" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          {/* Search Input */}
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg" />
            <input
              type="text"
              placeholder="Search by name, phone, or task title..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-700 border-0 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-gray-600 transition text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
            />
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 px-4 py-3 rounded-xl transition-colors"
          >
            <FiFilter className="text-lg" />
            Filters
            {(selectedDesignation || selectedStatus || fromDate || toDate) && (
              <span className="bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {[selectedDesignation, selectedStatus, fromDate, toDate].filter(Boolean).length}
              </span>
            )}
          </button>

          {/* Clear Filters */}
          {(selectedDesignation || selectedStatus || fromDate || toDate || searchTerm) && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-2 text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 px-4 py-3 rounded-xl transition-colors"
            >
              <FiX className="text-lg" />
              Clear
            </button>
          )}
        </div>

        {/* Expanded Filters */}
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Designation
              </label>
              <select
                value={selectedDesignation}
                onChange={(e) => {
                  setSelectedDesignation(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full bg-gray-50 dark:bg-gray-700 border-0 rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition"
              >
                <option value="">All Designations</option>
                {designations.map((d, i) => (
                  <option key={i} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Status
              </label>
              <select
                value={selectedStatus}
                onChange={(e) => {
                  setSelectedStatus(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full bg-gray-50 dark:bg-gray-700 border-0 rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="ongoing">Ongoing</option>
                <option value="complete">Complete</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                From Date
              </label>
              <div className="relative">
                <FiCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-700 border-0 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-gray-600 transition text-gray-900 dark:text-white"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                To Date
              </label>
              <div className="relative">
                <FiCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-700 border-0 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-gray-600 transition text-gray-900 dark:text-white"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Actions */}
      {selectedTasks.length > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-2xl p-4 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-medium">{selectedTasks.length}</span>
              </div>
              <p className="text-blue-800 dark:text-blue-200 font-medium">
                {selectedTasks.length} task{selectedTasks.length > 1 ? 's' : ''} selected
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleBulkUpdate("approved")}
                className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-xl transition-colors"
              >
                <FiCheck className="text-lg" />
                Bulk Approve
              </button>
              <button
                onClick={() => handleBulkUpdate("rejected")}
                className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-xl transition-colors"
              >
                <FiXCircle className="text-lg" />
                Bulk Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Results Count */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-gray-600 dark:text-gray-400">
          Showing {taskSubmissions.length} of {pagination?.total || 0} tasks
        </p>
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <span>Page {currentPage} of {totalPages || 1}</span>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th className="py-4 px-6 text-left text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={taskSubmissions.length > 0 && taskSubmissions.every((item) => selectedTasks.includes(item.id))}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="py-4 px-6 text-left text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  #
                </th>
                <th className="py-4 px-6 text-left text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Task Details
                </th>
                <th className="py-4 px-6 text-left text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <FiUser />
                    Employee
                  </div>
                </th>
                <th className="py-4 px-6 text-left text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <FiCalendar />
                    Date & Time
                  </div>
                </th>
                <th className="py-4 px-6 text-left text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <FiClock />
                    Task Period
                  </div>
                </th>
                <th className="py-4 px-6 text-left text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="py-4 px-6 text-left text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {error ? (
                <tr>
                  <td colSpan={8} className="py-8 px-6 text-center">
                    <div className="text-red-500 dark:text-red-400">
                      {error}
                    </div>
                  </td>
                </tr>
              ) : taskSubmissions.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-12 px-6 text-center">
                    <FiCalendar className="text-4xl text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400 text-lg mb-2">No task submissions found</p>
                    <p className="text-gray-400 dark:text-gray-500 text-sm">
                      Try adjusting your search or filters
                    </p>
                  </td>
                </tr>
              ) : (
                taskSubmissions.map((task, index) => (
                  <tr key={task.id || index} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                    {/* Checkbox */}
                    <td className="py-4 px-6">
                      <input
                        type="checkbox"
                        checked={selectedTasks.includes(task.id)}
                        onChange={() => toggleSelectOne(task.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>

                    {/* Serial Number */}
                    <td className="py-4 px-6 text-gray-600 dark:text-gray-400 text-sm">
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </td>

                    {/* Task Details */}
                    <td className="py-4 px-6">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {task.task_title || "-"}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          {task.task_name || "-"}
                        </p>
                        {task.photo && (
                          <button
                            onClick={() => window.open(task.photo, "_blank")}
                            className="flex items-center gap-1 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 text-xs mt-2"
                          >
                            <FiImage className="text-xs" />
                            View Photo
                          </button>
                        )}
                      </div>
                    </td>

                    {/* Employee Info */}
                    <td className="py-4 px-6">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {task.user_name || "-"}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                          <FiPhone className="text-xs" />
                          {task.user_phone || "-"}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                          <FiBriefcase className="text-xs" />
                          {task.designation || "-"}
                        </p>
                      </div>
                    </td>

                    {/* Date & Time */}
                    <td className="py-4 px-6">
                      <div className="text-sm">
                        <p className="text-gray-900 dark:text-white">
                          {formatIndianDate(task.date)}
                        </p>
                        <p className="text-gray-500 dark:text-gray-400">
                          {task.time || "-"}
                        </p>
                      </div>
                    </td>

                    {/* Task Period */}
                    <td className="py-4 px-6 text-sm text-gray-600 dark:text-gray-400">
                      {`${formatIndianDate(task.createTaskDate)} - ${formatIndianDate(task.endTaskDate)}`}
                    </td>

                    {/* Status */}
                    <td className="py-4 px-6">
                      {getStatusBadge(task.status)}
                    </td>

                    {/* Actions */}
                    <td className="py-4 px-6">
                      {task.status === "pending" ? (
                        <div className="flex flex-col gap-2">
                          <button
                            onClick={() => handleStatusUpdate(task.id, "approved")}
                            className="flex items-center gap-1 bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded-lg transition-colors text-xs"
                          >
                            <FiCheck className="text-xs" />
                            Approve
                          </button>
                          <button
                            onClick={() => handleStatusUpdate(task.id, "rejected")}
                            className="flex items-center gap-1 bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-lg transition-colors text-xs"
                          >
                            <FiXCircle className="text-xs" />
                            Reject
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleStatusUpdate(task.id, "pending")}
                          className="flex items-center gap-1 bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded-lg transition-colors text-xs"
                        >
                          <FiClock className="text-xs" />
                          Reset
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="flex items-center gap-2 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiChevronLeft />
            Previous
          </button>

          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <span>Page {currentPage} of {totalPages}</span>
          </div>

          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage >= totalPages}
            className="flex items-center gap-2 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <FiChevronRight />
          </button>
        </div>
      )}
    </div>
  );
};

export default Task;